import { useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/code';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import {
  Mail,
  Briefcase,
  CalendarDays,
  Wallet,
  FolderOpen,
  Phone,
  Send,
  Sparkles,
  CheckCircle2,
  User,
  AtSign,
  Loader2,
  CheckCircle,
  AlertCircle,
  Copy,
  Check,
} from 'lucide-react';
import axios from 'axios';
import PaperSEO from '../../components/paper/PaperSEO';

const API = process.env.REACT_APP_API_URL;
const EMAIL = 'pink@impink.co.kr';

const STEPS = [
  {
    icon: Briefcase,
    title: '프로젝트 소개',
    desc: '기획하고 계신 공연, 전시, 축제 등 프로젝트에 대한 간단한 소개를 적어주세요. 장르, 규모, 콘셉트 등 자유롭게 설명해 주시면 됩니다.',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-500/10',
    text: 'text-violet-400',
    border: 'border-violet-500/20',
  },
  {
    icon: CalendarDays,
    title: '희망 일정',
    desc: '프로젝트 진행 희망 시기를 알려주세요. 예) 3월 초, 4월 중순, 5월 말 등 대략적인 일정이면 충분합니다.',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-500/10',
    text: 'text-violet-400',
    border: 'border-violet-500/20',
  },
  {
    icon: Wallet,
    title: '예산 규모',
    desc: '대략적인 예산 범위를 알려주시면 최적의 제안을 드릴 수 있습니다. 미정인 경우 생략하셔도 됩니다.',
    color: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/20',
  },
  {
    icon: FolderOpen,
    title: '참고 자료',
    desc: '참고하실 포트폴리오나 기존 작업물, 레퍼런스 이미지 등이 있다면 함께 보내주세요.',
    color: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/20',
  },
  {
    icon: Phone,
    title: '연락처',
    desc: '담당자가 연락드릴 수 있도록 전화번호를 남겨주세요. 빠르고 정확한 상담을 위해 꼭 필요한 정보입니다.',
    color: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-500/10',
    text: 'text-rose-400',
    border: 'border-rose-500/20',
  },
];

const DEFAULT_BODY = `<h3>프로젝트 소개</h3>
<p style="color:#888;">공연/전시/축제 등 프로젝트에 대한 소개</p>
<p>&nbsp;</p>
<h3>희망 일정</h3>
<p style="color:#888;">예) 0월 초 / 중순 / 말</p>
<p>&nbsp;</p>
<h3>참고 자료</h3>
<p style="color:#888;">레퍼런스 링크 (없으면 생략 가능)</p>`;

export default function PaperContactPage() {
  const formRef = useRef(null);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [subject, setSubject] = useState('프로젝트 문의');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSend = async () => {
    if (!senderName.trim())
      return setResult({ type: 'error', message: '이름을 입력해 주세요.' });
    if (!senderEmail.trim())
      return setResult({ type: 'error', message: '이메일을 입력해 주세요.' });
    if (!subject.trim())
      return setResult({ type: 'error', message: '제목을 입력해 주세요.' });

    const stripped = body.replace(/<[^>]*>/g, '').trim();
    if (!stripped)
      return setResult({ type: 'error', message: '내용을 입력해 주세요.' });

    setSending(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('senderName', senderName.trim());
      formData.append('senderEmail', senderEmail.trim());
      formData.append('senderPhone', senderPhone.trim());
      formData.append('subject', subject.trim());
      formData.append('html', body);

      await axios.post(`${API}/api/paperWork/contact`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult({
        type: 'success',
        message: '문의가 성공적으로 전송되었습니다! 영업일 기준 1주일 이내에 연락드리겠습니다.',
      });
      setSenderName('');
      setSenderEmail('');
      setSenderPhone('');
      setSubject('프로젝트 문의');
      setBody(DEFAULT_BODY);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.';
      setResult({ type: 'error', message: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PaperSEO
        title="문의하기 | 핑크페이퍼"
        description="핑크페이퍼에 공연·축제·음원유통 관련 문의를 남겨주세요"
        path="/contact"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.15),transparent)]" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-10 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-xs font-medium mb-6">
            <Sparkles size={13} />
            Contact Us
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 leading-tight">
            프로젝트를 함께<br className="sm:hidden" />{' '}
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              만들어 보세요
            </span>
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            핑크페이퍼와 함께라면 공연, 전시, 축제 기획이 쉬워집니다.<br />
            아래 양식을 작성해 주시면 빠르게 연락드리겠습니다.
          </p>

          <div className="flex items-center justify-center gap-4 text-slate-600">
            <Briefcase size={28} />
            <CalendarDays size={24} />
            <Sparkles size={22} />
          </div>
        </div>
      </section>

      {/* Email Card */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 -mt-2 mb-12">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 p-[1px]">
          <div className="rounded-2xl bg-[#0d0b14] px-6 sm:px-10 py-8 sm:py-10">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0 shadow-lg shadow-violet-500/25">
                <Mail size={28} className="text-white" />
              </div>
              <div className="text-center sm:text-left flex-1 min-w-0">
                <p className="text-slate-400 text-sm mb-1">문의 이메일</p>
                <p className="text-white text-xl sm:text-2xl font-bold tracking-wide break-all">
                  {EMAIL}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors">
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? '복사됨' : '주소 복사'}
                </button>
                <button
                  onClick={scrollToForm}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-medium transition-colors shadow-lg shadow-violet-500/25">
                  <Send size={15} />
                  문의하기
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step Guide */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 mb-12">
        <div className="text-center mb-10">
          <h2 className="text-white text-2xl sm:text-3xl font-bold mb-3">
            이런 내용을 알려주세요
          </h2>
          <p className="text-slate-500 text-sm sm:text-base">
            아래 5가지 항목을 포함해 작성해 주시면 더 빠른 상담이 가능합니다.
          </p>
        </div>

        <div className="grid gap-4">
          {STEPS.map(({ icon: Icon, title, desc, color, bg, text, border }, i) => (
            <div
              key={title}
              className={`group relative rounded-2xl bg-[#1a1428] border ${border} hover:border-opacity-50 p-6 sm:p-7 transition-all duration-300 hover:translate-x-1`}>
              <div className="flex items-start gap-5">
                <div className="relative shrink-0">
                  <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center transition-transform group-hover:scale-110`}>
                    <Icon size={22} className={text} />
                  </div>
                  <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gradient-to-br ${color} text-white text-[10px] font-bold flex items-center justify-center shadow-lg`}>
                    {i + 1}
                  </span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-semibold text-base mb-1.5 flex items-center gap-2">
                    {title}
                    <CheckCircle2
                      size={14}
                      className={`${text} opacity-0 group-hover:opacity-100 transition-opacity`}
                    />
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section ref={formRef} className="max-w-4xl mx-auto px-4 sm:px-6 mb-16 scroll-mt-20">
        <div className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 p-[1px]">
          <div className="rounded-2xl bg-[#0d0b14] px-5 sm:px-10 py-8 sm:py-10">
            <h2 className="text-white text-xl sm:text-2xl font-bold mb-2 flex items-center gap-3">
              <Mail size={22} className="text-violet-400" />
              프로젝트 문의 보내기
            </h2>
            <p className="text-slate-500 text-sm mb-8">
              아래 양식을 작성하시면 {EMAIL}로 문의 메일이 전송됩니다.
            </p>

            {/* Input Fields */}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2">
                  <User size={13} />
                  이름 <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="홍길동"
                  className="w-full px-4 py-2.5 bg-[#0d0b14] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-violet-500/50 transition-colors"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2">
                  <AtSign size={13} />
                  이메일 <span className="text-rose-400">*</span>
                </label>
                <input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full px-4 py-2.5 bg-[#0d0b14] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-violet-500/50 transition-colors"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2">
                  <Phone size={13} />
                  연락처
                </label>
                <input
                  type="tel"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  placeholder="010-1234-5678"
                  className="w-full px-4 py-2.5 bg-[#0d0b14] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-violet-500/50 transition-colors"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2">
                  <Mail size={13} />
                  제목 <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="프로젝트 문의"
                  className="w-full px-4 py-2.5 bg-[#0d0b14] border border-white/10 rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:border-violet-500/50 transition-colors"
                />
              </div>
            </div>

            {/* TinyMCE Editor */}
            <div className="mb-6">
              <label className="flex items-center gap-1.5 text-slate-400 text-xs font-medium mb-2">
                <Mail size={13} />
                문의 내용 <span className="text-rose-400">*</span>
              </label>
              <Editor
                value={body}
                init={{
                  height: 340,
                  menubar: false,
                  toolbar: 'undo redo | bold italic underline | bullist numlist | link',
                  plugins: ['code', 'lists', 'link', 'autolink'],
                  skin: 'oxide-dark',
                  content_css: 'dark',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                  base_url: '/tinymce',
                  license_key: 'gpl',
                }}
                onEditorChange={(val) => setBody(val)}
              />
            </div>

            {/* Result Message */}
            {result && (
              <div className={`flex items-start gap-3 rounded-xl px-5 py-4 mb-6 text-sm ${
                result.type === 'success'
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                  : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
              }`}>
                {result.type === 'success' ? (
                  <CheckCircle size={18} className="shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                )}
                <p>{result.message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 disabled:from-slate-600 disabled:to-slate-600 rounded-xl text-white font-semibold text-sm transition-all shadow-lg shadow-violet-500/25 disabled:shadow-none disabled:cursor-not-allowed">
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  전송 중...
                </>
              ) : (
                <>
                  <Send size={16} />
                  문의 메일 보내기
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
